/* import __COLOCATED_TEMPLATE__ from './conversation-item.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import type {
  FinVoiceConversationEntryFunctionCall,
  FinVoiceConversationEntryMessage,
  FinVoiceConversationEntryStart,
  FinVoiceConversationEntryCallOutcome,
  FinVoiceConversationEntryEscalateToHuman,
  FinVoiceConversationEntrySearch,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-session';

export type FormattedConversationItem =
  | FormattedConversationMessage
  | FormattedConversationFunctionCall
  | FormattedConversationStart
  | FormattedConversationCallOutcome
  | FormattedConversationEscalateToHuman
  | FormattedConversationSearch;

export type FormattedConversationMessage = Pick<
  FinVoiceConversationEntryMessage,
  'status' | 'itemId' | 'type' | 'transcript' | 'errorCode'
> & {
  participant: 'user' | 'fin';
  timestamp: string;
};

export type FormattedConversationFunctionCall = Pick<
  FinVoiceConversationEntryFunctionCall,
  'status' | 'itemId' | 'type' | 'toolName' | 'toolCallId' | 'toolOutput' | 'toolArguments'
> & {
  timestamp: string;
};

export type FormattedConversationStart = Pick<
  FinVoiceConversationEntryStart,
  'status' | 'itemId' | 'type' | 'streamId' | 'voice' | 'pipeline'
> & {
  timestamp: string;
  vadSettings: string;
};

export type FormattedConversationCallOutcome = Pick<
  FinVoiceConversationEntryCallOutcome,
  | 'status'
  | 'itemId'
  | 'type'
  | 'outcome'
  | 'customDataAttributes'
  | 'intentClassification'
  | 'conversationSummary'
> & {
  timestamp: string;
};

export type FormattedConversationEscalateToHuman = Pick<
  FinVoiceConversationEntryEscalateToHuman,
  | 'status'
  | 'itemId'
  | 'type'
  | 'escalationContext'
  | 'customDataAttributes'
  | 'intentClassification'
> & {
  timestamp: string;
};

export type FormattedConversationSearch = Pick<
  FinVoiceConversationEntrySearch,
  'status' | 'itemId' | 'type' | 'searchId' | 'query' | 'articleSources'
> & {
  timestamp: string;
};

export interface ConversationArgs {
  conversationItem: FormattedConversationItem;
  previousItem?: FormattedConversationItem;
  nextItem?: FormattedConversationItem;
  verboseLogsEnabled: boolean;
}

interface Signature {
  Args: ConversationArgs;
  Element: HTMLElement;
}

export default class ConversationItem extends Component<Signature> {
  get containerStyling() {
    let styling: string[] = [];
    let { previousItem, nextItem, conversationItem } = this.args;

    let margin = 'mt-2';

    // Default margin is 6. Add a smaller margin if the types match
    if (previousItem?.type !== conversationItem.type) {
      margin = 'mt-5';
    }

    if (
      conversationItem.type === 'message' &&
      previousItem?.type === 'message' &&
      previousItem.participant !== conversationItem.participant
    ) {
      margin = 'mt-5';
    }

    styling.push(margin);

    if (conversationItem.type === 'message') {
      styling.push('flex flex-col gap-2 w-fit max-w-128 px-4 py-3 text-sm');

      if (conversationItem.participant === 'fin') {
        styling.push('bg-base-editor text-default mr-auto');
      } else {
        styling.push('bg-neutral-fill text-on-neutral text-right ml-auto');
      }

      // Border radius logic:
      // Check if previous/next messages are from same participant
      let hasPreviousSameParticipant =
        !!previousItem &&
        previousItem.type === 'message' &&
        previousItem.participant === conversationItem.participant;

      let hasNextSameParticipant =
        !!nextItem &&
        nextItem.type === 'message' &&
        nextItem.participant === conversationItem.participant;

      if (conversationItem.participant === 'user') {
        // User messages: bottom-right never rounded
        let borderClasses: string[] = [];

        // Top corners: rounded unless previous message is from same participant
        if (!hasPreviousSameParticipant) {
          borderClasses.push('rounded-tl-3xl', 'rounded-tr-3xl');
        }

        // Bottom-left: rounded unless next message is from same participant
        if (!hasNextSameParticipant) {
          borderClasses.push('rounded-bl-3xl');
        }

        styling.push(...borderClasses);
      } else {
        // Fin messages: bottom-left never rounded
        let borderClasses: string[] = [];

        // Top corners: rounded unless previous message is from same participant
        if (!hasPreviousSameParticipant) {
          borderClasses.push('rounded-tl-3xl', 'rounded-tr-3xl');
        }

        // Bottom-right: rounded unless next message is from same participant
        if (!hasNextSameParticipant) {
          borderClasses.push('rounded-br-3xl');
        }

        styling.push(...borderClasses);
      }
    }

    return styling.join(' ');
  }

  get transcriptStyling() {
    if (this.args.conversationItem.type !== 'message') {
      return '';
    }

    let styling: string[] = [];

    if (
      this.args.conversationItem.status === 'loading' ||
      this.args.conversationItem.status === 'error'
    ) {
      styling.push('italic');

      if (this.args.conversationItem.participant === 'fin') {
        styling.push('text-muted');
      }
    }

    return styling.join(' ');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::ConversationItem': typeof ConversationItem;
  }
}
